/**
 * SRES · Top-cut Review Panel — Sprint P37z UI (Feb 2026)
 * -------------------------------------------------------
 * Consumes the new backend endpoint
 *   GET /api/projects/{pid}/top-cut-review
 * and renders global + per-lithology top-cut recommendations.
 */
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Scissors, Info, ArrowRight } from "@phosphor-icons/react";
import { toast } from "sonner";

import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { WizardCard, KV, LoadingBanner } from "@/pages/wizard/_shared";

// Storage key used to pipe a suggested top-cut from this panel into
// CompositeBuilderPanel on /wizard/composite (P2 cross-panel wiring).
const LS_KEY = (pid) => `sres.composite.suggested_top_cut.${pid}`;

export default function TopCutReviewPanel({ projectId }) {
  const nav = useNavigate();
  const q = useQuery({
    queryKey: ["top-cut-review", projectId],
    queryFn:  () => api.get(`/projects/${projectId}/top-cut-review`)
                          .then((r) => r.data),
    enabled:  !!projectId,
    staleTime: 60_000,
  });

  if (q.isLoading) return <LoadingBanner label="Running top-cut analysis…" />;
  if (q.error) {
    const msg = q.error?.response?.data?.detail?.message
                    || q.error?.response?.data?.message
                    || q.error.message;
    return (
      <WizardCard title="Top-cut Review" testId="tcr-panel">
        <div className="text-[12px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2"
                data-testid="tcr-empty">
          {msg || "Top-cut review is unavailable for this project."}
        </div>
      </WizardCard>
    );
  }

  const d = q.data || {};
  const g = d.global || {};
  const perLitho = d.per_lithology || [];
  const stats     = d.per_lithology_stats || [];

  const applyToComposite = (value, label) => {
    try {
      localStorage.setItem(LS_KEY(projectId), String(value));
      toast.success(`Top-cut ${value} ${label ? "(" + label + ") " : ""}staged for Composite Builder.`);
    } catch { /* quota */ }
    nav(`/app/projects/${projectId}/wizard/composite`);
  };

  return (
    <WizardCard title="Top-cut Review — persistent recommendation"
                    testId="tcr-panel">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Global recommendation */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
            <Scissors size={12} /> Global recommendation
          </div>
          <div className="border border-slate-200 rounded p-3 bg-slate-50 space-y-1"
                  data-testid="tcr-global">
            <KV k="N total samples" v={d.n_total_samples?.toLocaleString?.() ?? "—"} />
            <KV k="Commodity" v={d.commodity ?? "—"} />
            <KV k="Grade unit" v={d.grade_unit ?? "—"} />
            <KV k="Suggested top-cut" v={fmt(g.suggested, 3) || "None"} />
            <KV k="P98" v={fmt(g.p98, 3)} />
            <KV k="P99" v={fmt(g.p99, 3)} />
            <KV k="Top-2 % contribution" v={g.top_2pct_contribution_pct != null
                            ? `${g.top_2pct_contribution_pct}%` : "—"} />
            <div className="text-[11px] text-slate-600 mt-1.5 italic">
              {g.reason || g.method || ""}
            </div>
            {g.suggested != null && Number.isFinite(Number(g.suggested)) && (
              <Button
                type="button" size="sm"
                onClick={() => applyToComposite(g.suggested, "global")}
                data-testid="tcr-apply-global"
                className="mt-2 h-7 text-[11px] gap-1 bg-brand-blue hover:bg-brand-blue/90 text-white"
              >
                Apply {fmt(g.suggested, 3)} to Composite Builder <ArrowRight size={11} weight="bold" />
              </Button>
            )}
          </div>
        </div>

        {/* Guidance card */}
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500">
            <Info size={12} /> Method & action
          </div>
          <div className="border border-slate-200 rounded p-3 bg-white space-y-2 text-[12px] text-slate-700">
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-0.5">
                Method
              </div>
              {d.guidance?.method || "—"}
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-widest text-slate-500 mb-0.5">
                Action
              </div>
              {d.guidance?.action || "—"}
            </div>
          </div>
        </div>
      </div>

      {/* Per-lithology grid */}
      {perLitho.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
            Per-lithology recommendations
          </div>
          <div className="border border-slate-200 rounded overflow-hidden text-[11.5px]"
                  data-testid="tcr-per-litho">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left px-2 py-1.5">Lithology</th>
                  <th className="text-right px-2 py-1.5">N samples</th>
                  <th className="text-right px-2 py-1.5">Suggested</th>
                  <th className="text-right px-2 py-1.5">P98</th>
                  <th className="text-right px-2 py-1.5">P99</th>
                  <th className="text-right px-2 py-1.5">Contribution %</th>
                  <th className="text-left  px-2 py-1.5">Reason</th>
                  <th className="text-right px-2 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {perLitho.map((r) => (
                  <tr key={r.lithology} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono text-[11px]">{r.lithology}</td>
                    <td className="px-2 py-1 text-right">{r.n_samples ?? "—"}</td>
                    <td className={`px-2 py-1 text-right ${r.suggested ? "font-semibold text-brand-blue" : "text-slate-400"}`}>
                      {r.suggested ? fmt(r.suggested, 3) : "None"}
                    </td>
                    <td className="px-2 py-1 text-right">{fmt(r.p98, 3)}</td>
                    <td className="px-2 py-1 text-right">{fmt(r.p99, 3)}</td>
                    <td className="px-2 py-1 text-right">
                      {r.contribution_pct != null ? `${r.contribution_pct}%` : "—"}
                    </td>
                    <td className="px-2 py-1 text-[10.5px] text-slate-500 max-w-md truncate"
                            title={r.reason}>{r.reason || "—"}</td>
                    <td className="px-2 py-1 text-right">
                      {r.suggested != null && Number.isFinite(Number(r.suggested)) && (
                        <button
                          type="button"
                          onClick={() => applyToComposite(r.suggested, r.lithology)}
                          data-testid={`tcr-apply-${r.lithology}`}
                          className="text-[10.5px] text-brand-blue hover:underline"
                        >
                          Apply →
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Per-lithology grade × length stats */}
      {stats.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-widest text-slate-500 mb-1.5">
            Grade × lithology (length-weighted)
          </div>
          <div className="border border-slate-200 rounded overflow-hidden text-[11.5px]"
                  data-testid="tcr-litho-stats">
            <table className="w-full">
              <thead className="bg-slate-100 text-slate-600">
                <tr>
                  <th className="text-left  px-2 py-1.5">Lithology</th>
                  <th className="text-right px-2 py-1.5">N samples</th>
                  <th className="text-right px-2 py-1.5">Total length (m)</th>
                  <th className="text-right px-2 py-1.5">Weighted grade</th>
                </tr>
              </thead>
              <tbody>
                {stats.map((r) => (
                  <tr key={r.lithology} className="border-t border-slate-100">
                    <td className="px-2 py-1 font-mono text-[11px]">{r.lithology}</td>
                    <td className="px-2 py-1 text-right">{r.n_samples ?? "—"}</td>
                    <td className="px-2 py-1 text-right">{fmt(r.total_length, 2)}</td>
                    <td className="px-2 py-1 text-right">{fmt(r.weighted_avg_grade, 4)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </WizardCard>
  );
}

function fmt(v, d = 2) {
  if (v == null || !Number.isFinite(Number(v))) return "—";
  return Number(v).toLocaleString(undefined, { maximumFractionDigits: d });
}
